// FO-046-P2 (batch-16) — portal document-upload met verplichte TL/HR-goedkeuring.
//
// Gedeelde DTO's + request-schemas (API <-> portal/backoffice). Eén bron: de API
// parseert ermee én het portal-form valideert er client-side mee vóór verzenden
// (CLAUDE.md §Validatie). Spiegelt `certificaat-aanvragen.ts`; het resultaat bij
// goedkeuring is een EmployeeDocument (het dossier), niet een certificaat.

import { z } from 'zod'

export type MedewerkerDocumentAanvraagStatus =
  | 'INGEDIEND'
  | 'GOEDGEKEURD'
  | 'AFGEKEURD'
  | 'INGETROKKEN'

export const MEDEWERKER_DOCUMENT_AANVRAAG_STATUS_LABELS: Record<
  MedewerkerDocumentAanvraagStatus,
  string
> = {
  INGEDIEND: 'Ingediend',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD: 'Afgewezen',
  INGETROKKEN: 'Ingetrokken',
}

// DocumentType (Prisma-enum) — het soort document dat de medewerker uploadt.
// Certificaten lopen via de aparte certificaat-aanvraag, NIET hier.
// batch-42 onboarding (FO-370) — loonheffingskorting + rijbewijs zijn nu ook
// aanvraagbaar/uploadbaar zodat het dossier proactief compleet wordt.
// batch-42 meldingen-fixes (FO-371 fix 2) — BEKEURING toegevoegd zodat de
// document-aanvraag-flow een bekeuring als soort kan kiezen.
export const MEDEWERKER_DOCUMENT_TYPES = [
  'LEGITIMATIE',
  'CONTRACT',
  'LOONHEFFINGSKORTING',
  'RIJBEWIJS',
  'BEKEURING',
  'OVERIG',
] as const
export type MedewerkerDocumentType = (typeof MEDEWERKER_DOCUMENT_TYPES)[number]

export const MEDEWERKER_DOCUMENT_TYPE_LABELS: Record<MedewerkerDocumentType, string> = {
  LEGITIMATIE:         'Legitimatie',
  CONTRACT:            'Contract',
  LOONHEFFINGSKORTING: 'Loonheffingskorting',
  RIJBEWIJS:           'Rijbewijs',
  BEKEURING:           'Bekeuring',
  OVERIG:              'Overig',
}

export interface MedewerkerDocumentAanvraagDTO {
  id: string
  employeeId: string
  employeeNaam: string | null
  employeeNumber: string | null
  documentType: MedewerkerDocumentType
  titel: string | null
  omschrijving: string | null
  status: MedewerkerDocumentAanvraagStatus
  beoordeeldDoorId: string | null
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  beoordelingNotitie: string | null
  resultaatDocumentId: string | null
  bestandNaam: string | null
  heeftBestand: boolean
  mimeType: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Tekstvelden van een nieuwe document-aanvraag (het bestand zit in de multipart-
 * part, niet in dit JSON-schema; de route eist het bestand apart). `.nullable()`
 * op de optionele velden zodat de read-DTO (null) round-tript zonder
 * VALIDATION_ERROR.
 */
export const CreateMedewerkerDocumentAanvraagSchema = z.object({
  documentType: z.enum(MEDEWERKER_DOCUMENT_TYPES),
  titel: z.string().trim().max(200, 'Maximaal 200 tekens').nullable().optional(),
  omschrijving: z.string().trim().max(2000, 'Maximaal 2000 tekens').nullable().optional(),
})
export type CreateMedewerkerDocumentAanvraagInput = z.infer<
  typeof CreateMedewerkerDocumentAanvraagSchema
>

/** Goedkeuren: optionele interne notitie; het bestand wordt een EmployeeDocument. */
export const GoedkeurMedewerkerDocumentAanvraagSchema = z.object({
  beoordelingNotitie: z.string().trim().max(2000, 'Maximaal 2000 tekens').nullable().optional(),
})
export type GoedkeurMedewerkerDocumentAanvraagInput = z.infer<
  typeof GoedkeurMedewerkerDocumentAanvraagSchema
>

/** Afwijzen: reden verplicht (de medewerker ziet 'm). */
export const AfwijzenMedewerkerDocumentAanvraagSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(5, 'Geef een reden van minimaal 5 tekens')
    .max(2000, 'Maximaal 2000 tekens'),
})
export type AfwijzenMedewerkerDocumentAanvraagInput = z.infer<
  typeof AfwijzenMedewerkerDocumentAanvraagSchema
>
